import _ from "lodash";
import React from "react";
import classes from "./PostDetailComp.scss";
import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import MediaModal from "UIComponents/MediaModal";
import UIButton from "UIComponents/UIButton";
import { getPostDetailQuery } from "Post/modules/PostQuery";
import { getRoutinePostDetailQuery } from "AppComponents/Routines/modules/RoutineQueries";
import { getPostDetailsFromCache } from "Post/modules/PostGraphqlHelpers";
import {
  createPostLike,
  deletePostLike,
  updatePostStatus,
} from "Journal/modules/JournalModule";
import Details from "../Details";
import { connect } from "react-redux";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  checkPostDetailsVisibilityPermission,
  hasTranslationPermission,
  getPostTranslationFilter,
  isPortfolioParityEnabled,
  isPortfolioParityEnabledAndIsJournalRoute,
  isPortfolioReviewSetupEnabledAndIsReviewRoute,
  getIsNewGoalTaggingEnabled,
} from "Post/utils";
import { CommentRemarkOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { colors } from "Constants";

const BLANK_ARRAY = [];
const BLANK_OBJECT = {};
const ModelContent = ({
  attachments,
  toggleMediaModal,
  showMediaModal,
  activeIndex,
  toggleDownloadBeginOverlay,
}) => {
  return showMediaModal ? (
    <MediaModal
      toggleMediaModal={toggleMediaModal}
      attachments={attachments}
      activeIndex={activeIndex}
      toggleDownloadBeginOverlay={toggleDownloadBeginOverlay}
    />
  ) : null;
};

const RejectComment = ({ rejectComment }) => {
  return !_.isEmpty(rejectComment) ? (
    <div className={classes.rejectCommentContainer}>
      <div className={classes.rejectCommentIcon}>
        <CommentRemarkOutlined />
      </div>
      <span className={classes.rejectCommentText}>{rejectComment}</span>
    </div>
  ) : null;
};

const loaderStyles = {
  loadingContainerStyle: {
    marginTop: "80%",
  },
  loadingContainerStyleForDetails: {
    marginTop: "100px",
  },
};

const styles = {
  titleStyle: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    fontWeight: 500,
  },
  containerStyle: {
    padding: "0px",
    borderBottom: `1px solid ${colors.strokeTwo} ! important`,
  },
};

class PostDetailComp extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showMediaModal: false,
      attachments: [],
      showDownloadBeginOverlay: false,
    };
    this.containerDiv = React.createRef();
  }

  componentDidMount = () => {
    const {
      detail = {},
      setToastMsg,
      onCloseClick,
      userInfo,
      updateRateOnLearningGoalsElements,
    } = this.props;
    const { isDeleted } = detail;

    // Check whether a user has permission to see the post details
    const hasPermission = checkPostDetailsVisibilityPermission({
      userInfo,
      postDetails: detail,
    });

    if (!hasPermission) {
      if (_.isFunction(onCloseClick)) onCloseClick();
      setToastMsg("toastMsgs:not_part_of_post");
    }

    //Handled if post is deleted
    if (isDeleted) {
      if (_.isFunction(onCloseClick)) onCloseClick();
      setToastMsg("toastMsgs:trying_to_access_deleted_post");
    }

    if (!_.isEmpty(detail) && _.isFunction(updateRateOnLearningGoalsElements)) {
      updateRateOnLearningGoalsElements({
        elementPYP: _.get(detail, "elementPYP", BLANK_ARRAY),
        propsIbPypElementRatings: _.get(
          detail,
          "ibPYPElementRatings",
          BLANK_ARRAY
        ),
      });
    }
  };

  componentDidUpdate = prevProps => {
    const { detail = {}, setToastMsg, onCloseClick, userInfo } = this.props;
    const { isDeleted } = detail;

    // Check permission on previous data
    const prevHasPermission = checkPostDetailsVisibilityPermission({
      userInfo,
      postDetails: _.get(prevProps, "detail", BLANK_OBJECT),
    });

    // Check whether a user has permission to see the post details
    const hasPermission = checkPostDetailsVisibilityPermission({
      userInfo,
      postDetails: detail,
    });

    // Close only if new data has different permission than previous one
    if (prevHasPermission != hasPermission && !hasPermission) {
      if (_.isFunction(onCloseClick)) onCloseClick();
      setToastMsg("toastMsgs:not_part_of_post");
    }

    //Handled if post is deleted and data is already in cache
    if (_.get(prevProps, "detail.isDeleted", false) != isDeleted && isDeleted) {
      if (_.isFunction(onCloseClick)) onCloseClick();
      setToastMsg("toastMsgs:trying_to_access_deleted_post");
    }
  };

  goToScrollTop = () => {
    if (this.containerDiv.current) {
      this.containerDiv.current.scrollTop = 0;
    }
  };

  toggleMediaModal = (value, activeIndex) => {
    const {
      detail: { attachments },
    } = this.props;

    this.setState({
      showMediaModal: value,
      attachments: value ? attachments : [],
      activeIndex: activeIndex,
    });
  };

  toggleDownloadBeginOverlay = () => {
    this.setState({ showDownloadBeginOverlay: true });
  };

  onPostLikedClick = ({ isLiked, activityId, activityType }) => {
    const { createPostLike, deletePostLike, detail, postId } = this.props;

    if (isLiked) {
      if (!_.isEmpty(activityId) && !_.isEmpty(activityType)) {
        deletePostLike({
          postId: activityId,
          parentType: activityType,
          postDetails: detail,
          fragmentName: "journalPostDetail",
        });
        return;
      }
      deletePostLike({
        postId,
        postDetails: detail,
        fragmentName: "journalPostDetail",
      });
    } else {
      if (!_.isEmpty(activityId) && !_.isEmpty(activityType)) {
        createPostLike({
          postId: activityId,
          parentType: activityType,
          postDetails: detail,
          fragmentName: "journalPostDetail",
        });
        return;
      }
      createPostLike({
        postId,
        postDetails: detail,
        fragmentName: "journalPostDetail",
      });
    }
  };

  onPostActionClick = postProps => {
    const {
      id,
      action,
      createdByName,
      params,
      activityId,
      activityType,
    } = postProps;
    const {
      mode = "edit",
      isCurrentAcademicYearSelected,
      isArchivedClass,
      isRemovedCourseForLoggedInUser,
      openedFromNotification,
      details: postDetails,
    } = this.props;
    switch (action) {
      case "LIKE":
        if (mode == "edit") {
          if (
            !isRemovedCourseForLoggedInUser &&
            !isArchivedClass &&
            isCurrentAcademicYearSelected
          ) {
            this.onPostLikedClick({
              id,
              isLiked: params.isLiked,
              activityId,
              activityType,
            });
          }
        }
        break;
      case "COMMENT":
        this.props.toggleCommentBox();
        break;
      case "PUBLISH":
      case "APPROVE":
        if (_.includes(window.location.href, "/routines")) {
          _.isFunction(this.props.updateRoutinePostStatus) &&
            this.props.updateRoutinePostStatus(
              id,
              "PUBLISHED",
              {},
              true,
              undefined,
              !openedFromNotification
            );
          return;
        }
        if (isPortfolioReviewSetupEnabledAndIsReviewRoute()) {
          this.props.updatePostStatus(
            id,
            "PUBLISHED",
            {},
            true,
            undefined,
            false,
            postDetails?.course?.id
          );
        } else {
          this.props.updatePostStatus(
            id,
            "PUBLISHED",
            {},
            true,
            undefined,
            !openedFromNotification //negate this to call the updatePostStatus query when called from notification
          );
        }
        if (this.props?.onCloseClick) this.props.onCloseClick();
        break;
      case "APPROVE_WITH_COMMENT": // for theatre mode, we have passed journal "onPostActionClick()" to this component
      case "REJECT_POST":
        if (_.isFunction(this.props?.onPostActionClick))
          this.props?.onPostActionClick({
            action,
            id,
            createdByName,
            ...(!_.isNil(params) ? params : {}),
          });
        break;
      case "POST_EDIT":
      case "DELETE":
      case "DRAFT":
      case "IN_APPROVAL":
        if (_.isFunction(this.props?.onPostActionClick))
          this.props?.onPostActionClick(postProps);
    }
  };

  render() {
    const {
      postId,
      onCloseClick,
      detail = {},
      showLoaderBar,
      fullMedia,
      grades,
      taggedStudents,
      userInfo: { id: userId },
      toggleCommentBox,
      toggleLEPreview,
      onClickEditPost,
      detailsContainerStyle,
      showCloseBtn,
      mediaMaxWidth,
      mediaMaxHeight,
      attachmentContainerStyle,
      hideViews,
      hideLike,
      hideComments,
      hideLearninGoals,
      hideUnitDetails,
      showTaggedFolders,
      courseIdFromQuery,
      portfolioStudentId,
      hideTaggedStudents,
      showPracticeStatus,
      isSidePaneContent = true,
      isViewFromPostPage = false,
      theatreActiveAttachmentIndex,
      setTheatreAttachmentIndex,
      theatreActiveAttachmentId,
      showAttachmentAsMediaCard = false,
      setTheatreAttachmentById,
      isRightPaneCard,
      openedFromNotification = false,
      fromPropsActivitySelectedStudentId = "",
      fromPropsHandleActivitySelectedStudentAttachmentsChange = () => {},
      staffEditAccess = true,
      cardType,
      canEditOrDelete,
      hideMoveToDraftOption,
      defaultAccodianExpanded,
      isProjectPostCard = false,
      canAddComments = true,
      openMediaOnLoad,
      hasManageAccess,
      hasAttachments,
      isRoutinePost = false,
      userType,
      hasRoutinesManageAccess,
      useDSV2,
      commentContainerClass,
      postCardStyles,
      isViewFromUnitPlanReflection,
    } = this.props;
    const {
      attachments,
      showMediaModal,
      activeIndex,
      showDownloadBeginOverlay,
    } = this.state;
    const state = _.get(detail, "state", "");

    const isPostApproveRejectEnabled = _.isEqual(state, "REJECTED");
    const rejectComment = _.get(detail, "rejectComment", "");

    const detailsClass = classNames({
      [classes.detailsV2]: isRoutinePost && !hasAttachments,
      [classes.routineContainerHeight]:
        isSidePaneContent && isRoutinePost && !hasAttachments,
      [classes.routineContainerWidth]:
        isRoutinePost &&
        isSidePaneContent &&
        hasAttachments &&
        !_.isEqual(userType, "staff"),
    });

    return [
      <div
        className={classes.container}
        ref={this.containerDiv}
        key="containerDiv"
      >
        <div className={classes.scrollContainer}>
          {isPostApproveRejectEnabled && (
            <RejectComment rejectComment={rejectComment} />
          )}
          <div className={detailsClass}>
            {!_.isNil(detail) && !_.isEmpty(detail) ? (
              <Details
                detailsContainerStyle={detailsContainerStyle}
                postId={postId}
                onCloseClick={onCloseClick}
                showCloseBtn={showCloseBtn}
                detail={detail}
                toggleMediaModal={this.toggleMediaModal}
                showLoaderBar={showLoaderBar}
                fullMedia={fullMedia}
                grades={grades}
                taggedStudents={taggedStudents}
                onPostActionClick={this.onPostActionClick}
                userId={userId}
                toggleCommentBox={toggleCommentBox}
                toggleLEPreview={toggleLEPreview}
                mediaMaxWidth={mediaMaxWidth}
                mediaMaxHeight={mediaMaxHeight}
                attachmentContainerStyle={attachmentContainerStyle}
                hideViews={hideViews}
                hideLike={hideLike}
                hideComments={hideComments}
                hideLearninGoals={hideLearninGoals}
                hideUnitDetails={
                  hideUnitDetails ||
                  (isPortfolioParityEnabledAndIsJournalRoute() &&
                    isSidePaneContent)
                }
                hideTaggedStudents={true}
                showTaggedFolders={showTaggedFolders}
                courseIdFromQuery={courseIdFromQuery}
                portfolioStudentId={portfolioStudentId}
                showPracticeStatus={showPracticeStatus}
                showDownloadBeginOverlay={showDownloadBeginOverlay}
                isSidePaneContent={isSidePaneContent}
                isViewFromPostPage={isViewFromPostPage}
                theatreActiveAttachmentIndex={theatreActiveAttachmentIndex}
                setTheatreAttachmentIndex={setTheatreAttachmentIndex}
                theatreActiveAttachmentId={theatreActiveAttachmentId}
                showAttachmentAsMediaCard={showAttachmentAsMediaCard}
                setTheatreAttachmentById={setTheatreAttachmentById}
                isRightPaneCard={isRightPaneCard}
                openedFromNotification={openedFromNotification}
                fromPropsActivitySelectedStudentId={
                  fromPropsActivitySelectedStudentId
                }
                fromPropsHandleActivitySelectedStudentAttachmentsChange={
                  fromPropsHandleActivitySelectedStudentAttachmentsChange
                }
                staffEditAccess={staffEditAccess}
                loadingContainerStyle={
                  loaderStyles.loadingContainerStyleForDetails
                }
                sourceType={_.get(this.props, "source_type")}
                cardType={cardType}
                canEditOrDelete={canEditOrDelete}
                hideMoveToDraftOption={hideMoveToDraftOption}
                defaultAccodianExpanded={defaultAccodianExpanded}
                isProjectPostCard={isProjectPostCard}
                canAddComments={canAddComments}
                customTextStyle={
                  isPortfolioParityEnabled() && !isProjectPostCard
                    ? styles.titleStyle
                    : BLANK_OBJECT
                }
                customContainerStyle={
                  isPortfolioParityEnabled() && !isProjectPostCard
                    ? styles.containerStyle
                    : BLANK_OBJECT
                }
                openMediaOnLoad={openMediaOnLoad}
                showLeAndUnitDetailsV2={
                  (isPortfolioParityEnabledAndIsJournalRoute() ||
                    isPortfolioReviewSetupEnabledAndIsReviewRoute() ||
                    getIsNewGoalTaggingEnabled()) &&
                  isSidePaneContent
                }
                hasManageAccess={hasManageAccess}
                showRoutineDetails={this.props.showRoutineDetails}
                hasRoutinesManageAccess={hasRoutinesManageAccess}
                useDSV2={useDSV2}
                commentContainerClass={commentContainerClass}
                postCardStyles={postCardStyles}
                isViewFromUnitPlanReflection={isViewFromUnitPlanReflection}
              />
            ) : null}
          </div>
          <ModelContent
            attachments={attachments}
            toggleMediaModal={this.toggleMediaModal}
            showMediaModal={showMediaModal}
            activeIndex={activeIndex}
            toggleDownloadBeginOverlay={this.toggleDownloadBeginOverlay}
          />
        </div>
        {onClickEditPost && (
          <div className={classes.footer}>
            <UIButton
              color={"pink"}
              onClick={onClickEditPost}
              size={"lg"}
              containerStyle={{ width: "100%" }}
            >{`Edit post`}</UIButton>
          </div>
        )}
      </div>,
    ];
  }
}

const mapActionCreators = {
  createPostLike,
  deletePostLike,
  updatePostStatus,
  setToastMsg,
};

const mapStateToProps = (state, ownProps) => {
  const translationFilter = getPostTranslationFilter({
    userInfo: _.get(state, "login.userInfo", BLANK_OBJECT),
    shouldTranslate: false,
    handleTranslation: hasTranslationPermission(),
  });
  const isCurrentAcademicYearSelected = _.get(
    state,
    "platform.academicYearSelected.isCurrentAcademicYear",
    true
  );
  const isArchivedClass = _.get(
    state,
    "teacher.selected_class.isArchivedClass"
  );
  const academicYearId = getSelectedAcademicYearId(state);

  const course = getCourseDetailsFromCache({
    id: state.teacher.selected_class.selected_course,
    courseGradeFilters: { academicYearId },
  });
  const isRemovedCourseForLoggedInUser = _.get(
    course,
    "isRemovedCourseForLoggedInUser",
    false
  );

  const skipPostDetailQuery = _.includes(window.location.href, "/routines");
  const userType = _.get(state, "login.userInfo.user_type", "");
  return {
    isData: true,
    isLoading: false,
    showLoaderBar: false,
    portfolioStudentId: ownProps?.portfolioStudentId,
    userInfo: state.login.userInfo,
    translationFilter,
    isCurrentAcademicYearSelected,
    isArchivedClass,
    isRemovedCourseForLoggedInUser,
    ...loaderStyles,
    skipPostDetailQuery,
    userType,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getPostDetailQuery, {
    name: "getPostDetail",
    skip: ({ postId, skipPostDetailQuery }) =>
      _.isEmpty(_.toString(postId)) || skipPostDetailQuery,
    options: ({ postId, translationFilter }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: postId,
        translationFilter: translationFilter,
      },
    }),
    props({
      getPostDetail,
      ownProps: {
        postId,
        translationFilter,
        isData,
        isLoading,
        canEditOrDelete,
        userInfo,
      },
    }) {
      const postDetails = getPostDetailsFromCache({
        postId,
        translationFilter,
      });
      const subjects = _.get(
        postDetails,
        "unitPlan.subjects.value",
        BLANK_ARRAY
      );
      return {
        subjects,
        taggedStudents: _.get(postDetails, "students.edges", BLANK_ARRAY),
        isData: !_.isEmpty(postDetails) && isData,
        networkStatus: getPostDetail.networkStatus,
        isLoading:
          getPostDetail["networkStatus"] == 1 ||
          getPostDetail["networkStatus"] == 2 ||
          getPostDetail["networkStatus"] == 4 ||
          isLoading,
        detail: postDetails,
        canEditOrDelete: _.isNil(canEditOrDelete)
          ? _.get(userInfo, "user_type") === "staff" ||
            postDetails?.createdBy?.id === userInfo?.id
          : canEditOrDelete,
      };
    },
  }),
  graphql(getRoutinePostDetailQuery, {
    name: "getRoutinePostDetail",
    skip: ({ postId, skipPostDetailQuery }) =>
      _.isEmpty(_.toString(postId)) || !skipPostDetailQuery,
    options: ({ postId, translationFilter }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: postId,
        // translationFilter: translationFilter,
      },
    }),
    props({
      getRoutinePostDetail,
      ownProps: {
        postId,
        translationFilter,
        isData,
        isLoading,
        canEditOrDelete,
        userInfo,
      },
    }) {
      const postDetails = _.get(getRoutinePostDetail, "node", {});
      const subjects = _.get(
        postDetails,
        "unitPlan.subjects.value",
        BLANK_ARRAY
      );
      return {
        subjects,
        taggedStudents: _.get(postDetails, "students.edges", BLANK_ARRAY),
        isData: !_.isEmpty(postDetails) && isData,
        networkStatus: getRoutinePostDetail.networkStatus,
        isLoading:
          getRoutinePostDetail["networkStatus"] == 1 ||
          getRoutinePostDetail["networkStatus"] == 2 ||
          getRoutinePostDetail["networkStatus"] == 4 ||
          isLoading,
        detail: postDetails,
        canEditOrDelete: _.isNil(canEditOrDelete)
          ? _.get(userInfo, "user_type") === "staff" ||
            postDetails?.createdBy?.id === userInfo?.id
          : canEditOrDelete,
      };
    },
  }),
  withLoader
)(PostDetailComp);
